import React from "react";
import { MapPin, Phone, Clock, Mail, MessageCircle, ArrowRight, Facebook } from "lucide-react";
import { CIMA, openWhatsapp } from "@/lib/cima";

export default function Contact({ onOpenQuote }) {
  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="py-20 lg:py-28 bg-[var(--cima-surface)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Info column */}
          <div className="lg:col-span-5">
            <span className="divider-orange mb-5" />
            <p className="font-mono-cima text-xs uppercase tracking-widest text-[var(--cima-red)] mb-3">
              Contacto
            </p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight text-[var(--cima-navy)]">
              Estamos listos<br />para atenderte.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Solicita tu cotización, agenda una entrega o pide asesoría técnica. Nuestro equipo te
              responde directamente.
            </p>

            <div className="mt-8 space-y-px bg-slate-200 border border-slate-200">
              <a
                href={`tel:${CIMA.phoneTel}`}
                data-testid="contact-phone-link"
                className="flex items-start gap-4 p-5 bg-white hover:bg-[var(--cima-surface-2)] transition-colors"
              >
                <div className="w-10 h-10 bg-[var(--cima-red)] text-white flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] font-mono-cima uppercase tracking-widest text-slate-500">
                    Teléfono
                  </div>
                  <div className="font-display font-semibold text-lg text-[var(--cima-navy)]">
                    {CIMA.phone}
                  </div>
                </div>
              </a>

              <button
                type="button"
                onClick={() => openWhatsapp("Hola CIMA, me interesa más información.")}
                data-testid="contact-whatsapp-link"
                className="w-full text-left flex items-start gap-4 p-5 bg-white hover:bg-[var(--cima-surface-2)] transition-colors"
              >
                <div className="w-10 h-10 bg-[#16A34A] text-white flex items-center justify-center shrink-0">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] font-mono-cima uppercase tracking-widest text-slate-500">
                    WhatsApp
                  </div>
                  <div className="font-display font-semibold text-lg text-[var(--cima-navy)]">
                    {CIMA.whatsapp}
                  </div>
                </div>
              </button>

              <a
                href={CIMA.mapsLink}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="contact-address-link"
                className="flex items-start gap-4 p-5 bg-white hover:bg-[var(--cima-surface-2)] transition-colors"
              >
                <div className="w-10 h-10 bg-[var(--cima-navy)] text-white flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] font-mono-cima uppercase tracking-widest text-slate-500">
                    Ubicación
                  </div>
                  <div className="font-display font-semibold text-lg text-[var(--cima-navy)] leading-tight">
                    {CIMA.address}
                  </div>
                </div>
              </a>

              <div className="flex items-start gap-4 p-5 bg-white">
                <div className="w-10 h-10 bg-[var(--cima-orange)] text-white flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] font-mono-cima uppercase tracking-widest text-slate-500">
                    Horario
                  </div>
                  <div className="font-display font-semibold text-base text-[var(--cima-navy)] leading-tight">
                    {CIMA.hoursFull}
                  </div>
                </div>
              </div>

              <a
                href={`mailto:${CIMA.email}`}
                data-testid="contact-email-link"
                className="flex items-start gap-4 p-5 bg-white hover:bg-[var(--cima-surface-2)] transition-colors"
              >
                <div className="w-10 h-10 bg-slate-800 text-white flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] font-mono-cima uppercase tracking-widest text-slate-500">
                    Correo
                  </div>
                  <div className="font-display font-semibold text-base text-[var(--cima-navy)] break-all">
                    {CIMA.email}
                  </div>
                </div>
              </a>

              <a
                href={CIMA.facebookUrl}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="contact-facebook-link"
                className="flex items-start gap-4 p-5 bg-white hover:bg-[var(--cima-surface-2)] transition-colors"
              >
                <div className="w-10 h-10 bg-[#1877F2] text-white flex items-center justify-center shrink-0">
                  <Facebook className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] font-mono-cima uppercase tracking-widest text-slate-500">
                    Facebook
                  </div>
                  <div className="font-display font-semibold text-base text-[var(--cima-navy)]">
                    Síguenos en Facebook
                  </div>
                </div>
              </a>
            </div>

            <button
              data-testid="contact-quote-button"
              onClick={onOpenQuote}
              className="btn-cima-red mt-8 w-full sm:w-auto px-7 py-3.5 text-sm font-semibold uppercase tracking-wide flex items-center justify-center gap-2"
            >
              Solicitar Cotización Inmediata
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Map column */}
          <div className="lg:col-span-7">
            <div className="border border-slate-300 p-3 bg-white h-full">
              <div className="relative aspect-[4/3] lg:aspect-auto lg:h-full w-full">
                <iframe
                  data-testid="contact-map"
                  title="Ubicación de CIMA - Zihuatanejo"
                  src={CIMA.mapsEmbed}
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: "420px" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                {/* Custom CIMA pin overlay */}
                <a
                  href={CIMA.mapsLink}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="map-cima-pin"
                  aria-label="Abrir ubicación de CIMA en Google Maps"
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full pointer-events-auto group"
                >
                  <div className="relative flex flex-col items-center">
                    {/* Logo bubble */}
                    <div className="bg-white border-2 border-[var(--cima-red)] shadow-2xl px-3 py-2 flex items-center gap-2 group-hover:scale-105 transition-transform">
                      <img
                        src={CIMA.logoUrl}
                        alt="CIMA"
                        className="h-9 sm:h-10 w-auto object-contain"
                      />
                    </div>
                    {/* Pin tail */}
                    <div className="w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[14px] border-t-[var(--cima-red)] -mt-px" />
                    {/* Ground pulse */}
                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-6 h-2 bg-[var(--cima-red)]/40 rounded-full blur-sm map-pin-pulse" />
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
